import PropTypes from "prop-types";

/* ═══════════════════════════════════════════════════════════════
   SCADA Component SVG Previews
   Used in Add Item modal and ComponentConfigModal preview area.
   Each preview is a standalone SVG matching the canvas node appearance.
   ═══════════════════════════════════════════════════════════════ */

const PumpPreview = ({ color = "#3b82f6", size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="40" r="32" fill={`${color}15`} stroke={color} strokeWidth="2.5" />
    <path d="M40 12 L40 40 L60 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
    <path d="M40 68 L40 40 L20 60" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
    <path d="M12 40 L40 40 L20 20" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
    <path d="M68 40 L40 40 L60 60" stroke={color} strokeWidth="2" strokeLinecap="round" fill={`${color}15`} />
    <circle cx="40" cy="40" r="6" fill={color} />
    <rect x="0" y="35" width="8" height="10" fill={color} rx="2" />
    <rect x="35" y="0" width="10" height="8" fill={color} rx="2" />
  </svg>
);
PumpPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const TankPreview = ({ color = "#06b6d4", size = 80 }) => {
  const s = size / 100;
  return (
    <svg width={70 * s} height={size} viewBox="0 0 70 100" fill="none">
      <rect x="8" y="20" width="54" height="60" rx="3" fill={`${color}10`} stroke={color} strokeWidth="2" />
      <rect x="10" y="50" width="50" height="30" fill={`${color}35`} rx="2" />
      <ellipse cx="35" cy="20" rx="27" ry="8" fill={`${color}10`} stroke={color} strokeWidth="2" />
      <ellipse cx="35" cy="80" rx="27" ry="8" fill={`${color}10`} stroke={color} strokeWidth="2" />
      <text x="35" y="70" textAnchor="middle" fontSize="11" fontWeight="600" fill={color}>60%</text>
      <rect x="0" y="35" width="8" height="6" fill={color} rx="1" />
      <rect x="32" y="88" width="6" height="12" fill={color} rx="1" />
    </svg>
  );
};
TankPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const ValvePreview = ({ color = "#f59e0b", size = 80 }) => {
  const s = size / 60;
  return (
    <svg width={70 * s} height={size} viewBox="0 0 70 60" fill="none">
      <polygon points="5,10 35,30 5,50" fill={`${color}25`} stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <polygon points="65,10 35,30 65,50" fill={`${color}25`} stroke={color} strokeWidth="2" strokeLinejoin="round" />
      <line x1="35" y1="5" x2="35" y2="30" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="35" cy="5" r="4" fill="none" stroke={color} strokeWidth="2" />
      <circle cx="35" cy="30" r="4" fill="#22c55e" />
      <rect x="0" y="27" width="5" height="6" fill={color} rx="1" />
      <rect x="65" y="27" width="5" height="6" fill={color} rx="1" />
    </svg>
  );
};
ValvePreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const MotorPreview = ({ color = "#8b5cf6", size = 80 }) => {
  const s = size / 60;
  return (
    <svg width={90 * s} height={size} viewBox="0 0 90 60" fill="none">
      <rect x="10" y="10" width="50" height="40" rx="4" fill={`${color}12`} stroke={color} strokeWidth="2" />
      <text x="35" y="36" textAnchor="middle" fontSize="16" fontWeight="bold" fill={color}>M</text>
      <circle cx="70" cy="30" r="10" fill={`${color}15`} stroke={color} strokeWidth="2" />
      <line x1="60" y1="30" x2="80" y2="30" stroke={color} strokeWidth="2" />
      <line x1="12" y1="15" x2="12" y2="45" stroke={color} strokeWidth="1" opacity="0.4" />
      <line x1="18" y1="15" x2="18" y2="45" stroke={color} strokeWidth="1" opacity="0.4" />
      <circle cx="10" cy="10" r="4" fill="#22c55e" />
      <rect x="28" y="2" width="14" height="8" rx="2" fill={`${color}20`} stroke={color} strokeWidth="1.5" />
    </svg>
  );
};
MotorPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const ConveyerPreview = ({ color = "#64748b", size = 80 }) => (
  <svg width={size * 1.2} height={size * 0.6} viewBox="0 0 100 50" fill="none">
    <rect x="5" y="12" width="90" height="20" rx="10" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <circle cx="15" cy="22" r="6" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
    <circle cx="85" cy="22" r="6" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
    <rect x="30" y="8" width="8" height="6" rx="1" fill={`${color}50`} />
    <rect x="55" y="8" width="8" height="6" rx="1" fill={`${color}50`} />
    <polygon points="92,22 86,18 86,26" fill={color} opacity="0.6" />
    <circle cx="8" cy="8" r="3" fill="#22c55e" />
  </svg>
);
ConveyerPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const AHUPreview = ({ color = "#10b981", size = 80 }) => (
  <svg width={size} height={size * 0.85} viewBox="0 0 80 70" fill="none">
    <rect x="5" y="5" width="70" height="50" rx="4" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <text x="15" y="18" fontSize="9" fontWeight="bold" fill={color} opacity="0.5">AHU</text>
    <ellipse cx="45" cy="20" rx="4" ry="12" fill={`${color}30`} stroke={color} strokeWidth="1" />
    <ellipse cx="45" cy="46" rx="4" ry="12" fill={`${color}30`} stroke={color} strokeWidth="1" />
    <ellipse cx="32" cy="33" rx="12" ry="4" fill={`${color}30`} stroke={color} strokeWidth="1" />
    <ellipse cx="58" cy="33" rx="12" ry="4" fill={`${color}30`} stroke={color} strokeWidth="1" />
    <circle cx="45" cy="33" r="4" fill={color} />
    <rect x="0" y="28" width="5" height="10" fill={color} rx="2" />
    <rect x="75" y="28" width="5" height="10" fill={color} rx="2" />
    <circle cx="70" cy="10" r="3" fill="#22c55e" />
  </svg>
);
AHUPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const BlowerFanPreview = ({ color = "#6366f1", size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <circle cx="40" cy="40" r="30" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <ellipse cx="40" cy="18" rx="6" ry="16" fill={`${color}25`} stroke={color} strokeWidth="1.5" />
    <ellipse cx="40" cy="62" rx="6" ry="16" fill={`${color}25`} stroke={color} strokeWidth="1.5" />
    <ellipse cx="18" cy="40" rx="16" ry="6" fill={`${color}25`} stroke={color} strokeWidth="1.5" />
    <ellipse cx="62" cy="40" rx="16" ry="6" fill={`${color}25`} stroke={color} strokeWidth="1.5" />
    <circle cx="40" cy="40" r="6" fill={color} />
    <circle cx="40" cy="40" r="3" fill="#fff" />
  </svg>
);
BlowerFanPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const BatteryPreview = ({ color = "#22c55e", size = 80 }) => (
  <svg width={size * 0.7} height={size} viewBox="0 0 50 70" fill="none">
    <rect x="5" y="10" width="40" height="55" rx="4" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <rect x="15" y="4" width="20" height="8" rx="2" fill={`${color}20`} stroke={color} strokeWidth="1.5" />
    <rect x="10" y="35" width="30" height="25" rx="2" fill={`${color}35`} />
    <text x="25" y="52" textAnchor="middle" fontSize="10" fontWeight="700" fill={color}>75%</text>
    <line x1="20" y1="22" x2="30" y2="22" stroke={color} strokeWidth="2" />
    <line x1="25" y1="17" x2="25" y2="27" stroke={color} strokeWidth="2" />
  </svg>
);
BatteryPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const ChillerPreview = ({ color = "#0ea5e9", size = 80 }) => (
  <svg width={size} height={size * 0.85} viewBox="0 0 80 70" fill="none">
    <rect x="5" y="5" width="70" height="50" rx="4" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <path d="M20 30 Q25 15 30 30 Q35 45 40 30 Q45 15 50 30 Q55 45 60 30" stroke={color} strokeWidth="2" fill="none" />
    <line x1="15" y1="45" x2="65" y2="45" stroke={color} strokeWidth="1" strokeDasharray="3 3" opacity="0.4" />
    <rect x="0" y="22" width="5" height="10" fill={color} rx="2" />
    <rect x="75" y="22" width="5" height="10" fill={color} rx="2" />
    <text x="40" y="64" textAnchor="middle" fontSize="8" fontWeight="600" fill={color}>CHILLER</text>
  </svg>
);
ChillerPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const MSBPreview = ({ color = "#f97316", size = 80 }) => (
  <svg width={size} height={size * 0.85} viewBox="0 0 80 70" fill="none">
    <rect x="5" y="5" width="70" height="55" rx="3" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <line x1="5" y1="18" x2="75" y2="18" stroke={color} strokeWidth="1.5" />
    <text x="40" y="14" textAnchor="middle" fontSize="8" fontWeight="bold" fill={color}>MSB</text>
    {[20, 35, 50].map((x) => (
      <g key={x}>
        <rect x={x} y="24" width="12" height="16" rx="2" fill={`${color}15`} stroke={color} strokeWidth="1" />
        <circle cx={x + 6} cy="46" r="3" fill="#22c55e" />
      </g>
    ))}
    <rect x="0" y="30" width="5" height="8" fill={color} rx="1" />
    <rect x="75" y="30" width="5" height="8" fill={color} rx="1" />
  </svg>
);
MSBPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const TransformerPreview = ({ color = "#a855f7", size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <circle cx="28" cy="40" r="18" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <circle cx="52" cy="40" r="18" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <text x="28" y="44" textAnchor="middle" fontSize="10" fontWeight="bold" fill={color}>P</text>
    <text x="52" y="44" textAnchor="middle" fontSize="10" fontWeight="bold" fill={color}>S</text>
    <rect x="0" y="36" width="10" height="8" fill={color} rx="2" />
    <rect x="70" y="36" width="10" height="8" fill={color} rx="2" />
    <path d="M36 28 L44 28" stroke={color} strokeWidth="1.5" strokeDasharray="2 2" opacity="0.5" />
    <path d="M36 52 L44 52" stroke={color} strokeWidth="1.5" strokeDasharray="2 2" opacity="0.5" />
  </svg>
);
TransformerPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const SwitchgearPreview = ({ color = "#ef4444", size = 80 }) => (
  <svg width={size} height={size * 0.85} viewBox="0 0 80 70" fill="none">
    <rect x="5" y="5" width="70" height="55" rx="3" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <line x1="5" y1="18" x2="75" y2="18" stroke={color} strokeWidth="1.5" />
    <text x="40" y="14" textAnchor="middle" fontSize="7" fontWeight="bold" fill={color}>SWITCHGEAR</text>
    <line x1="25" y1="30" x2="25" y2="48" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="25" cy="28" r="3" fill={color} />
    <circle cx="25" cy="50" r="3" fill={color} />
    <line x1="45" y1="28" x2="55" y2="48" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
    <circle cx="45" cy="28" r="3" fill="#22c55e" />
    <circle cx="55" cy="50" r="3" fill={color} />
  </svg>
);
SwitchgearPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const SplitACPreview = ({ color = "#14b8a6", size = 80 }) => (
  <svg width={size} height={size * 0.55} viewBox="0 0 90 50" fill="none">
    <rect x="5" y="5" width="80" height="30" rx="15" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <line x1="15" y1="35" x2="75" y2="35" stroke={color} strokeWidth="1" opacity="0.3" />
    <line x1="25" y1="35" x2="25" y2="45" stroke={color} strokeWidth="1.5" opacity="0.4" />
    <line x1="45" y1="35" x2="45" y2="48" stroke={color} strokeWidth="1.5" opacity="0.3" />
    <line x1="65" y1="35" x2="65" y2="45" stroke={color} strokeWidth="1.5" opacity="0.4" />
    <circle cx="72" cy="12" r="3" fill="#22c55e" />
    <text x="45" y="24" textAnchor="middle" fontSize="8" fontWeight="600" fill={color}>A/C</text>
  </svg>
);
SplitACPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const HighbayLampPreview = ({ color = "#eab308", size = 80 }) => (
  <svg width={size * 0.7} height={size} viewBox="0 0 50 70" fill="none">
    <rect x="18" y="0" width="14" height="12" rx="2" fill={`${color}20`} stroke={color} strokeWidth="1.5" />
    <line x1="25" y1="12" x2="25" y2="18" stroke={color} strokeWidth="2" />
    <path d="M10 18 L40 18 L35 50 L15 50 Z" fill={`${color}15`} stroke={color} strokeWidth="2" strokeLinejoin="round" />
    {[0, 1, 2].map((i) => (
      <line key={i} x1={18 + i * 6} y1="52" x2={14 + i * 8} y2="65" stroke={`${color}60`} strokeWidth="1.5" strokeLinecap="round" />
    ))}
    <circle cx="25" cy="34" r="6" fill={`${color}40`} />
  </svg>
);
HighbayLampPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const SolarPanelPreview = ({ color = "#f59e0b", size = 80 }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 80 56" fill="none">
    <rect x="5" y="5" width="70" height="40" rx="2" fill={`${color}10`} stroke={color} strokeWidth="2" />
    {[25, 45].map((x) => <line key={`v${x}`} x1={x} y1="5" x2={x} y2="45" stroke={color} strokeWidth="1" opacity="0.3" />)}
    {[18, 32].map((y) => <line key={`h${y}`} x1="5" y1={y} x2="75" y2={y} stroke={color} strokeWidth="1" opacity="0.3" />)}
    <rect x="35" y="45" width="10" height="8" fill={color} rx="1" />
    <circle cx="68" cy="10" r="3" fill="#22c55e" />
  </svg>
);
SolarPanelPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

const SolarInverterPreview = ({ color = "#d97706", size = 80 }) => (
  <svg width={size * 0.8} height={size} viewBox="0 0 60 75" fill="none">
    <rect x="5" y="10" width="50" height="50" rx="4" fill={`${color}10`} stroke={color} strokeWidth="2" />
    <path d="M15 35 Q20 20 25 35 Q30 50 35 35" stroke={color} strokeWidth="2" fill="none" />
    <line x1="38" y1="28" x2="48" y2="28" stroke={color} strokeWidth="2" />
    <line x1="38" y1="35" x2="48" y2="35" stroke={color} strokeWidth="2" />
    <line x1="38" y1="42" x2="48" y2="42" stroke={color} strokeWidth="2" />
    <text x="30" y="68" textAnchor="middle" fontSize="7" fontWeight="600" fill={color}>INV</text>
    <rect x="20" y="2" width="20" height="8" rx="2" fill={`${color}15`} stroke={color} strokeWidth="1" />
    <circle cx="48" cy="16" r="3" fill="#22c55e" />
  </svg>
);
SolarInverterPreview.propTypes = { color: PropTypes.string, size: PropTypes.number };

/* ═══════════════════════════════════════════════════════════════
   Realistic Image Map for 3D SCADA Components
   ═══════════════════════════════════════════════════════════════ */

export const REALISTIC_IMAGE_MAP = {
  pumps: "/scada-realistic/Pump.svg",
  pump: "/scada-realistic/Pump.svg",
  conveyer: "/scada-realistic/Conveyor.png",
  motor: "/scada-realistic/Motor.png",
  tanks: "/scada-realistic/tank.png",
  tank: "/scada-realistic/tank.png",
  chiller: "/scada-realistic/Chiller.png",
  ahu: "/scada-realistic/AHU.png",
  blowerFan: "/scada-realistic/blower.png",
  battery: "/scada-realistic/Battery.png",
  msb: "/scada-realistic/MSB.png",
  transformer: "/scada-realistic/Transformer.png",
  switchgear: "/scada-realistic/switchgear.png",
  splitACIndoor: "/scada-realistic/split-ac.png",
  splitAc: "/scada-realistic/split-ac.png",
  highbayLamp: "/scada-realistic/highbay-lamp.png",
  solarPanel: "/scada-realistic/solar-panel.png",
  solarInverter: "/scada-realistic/solar-inverter.png",
};

export const getRealisticPreviewImage = (type) => {
  return REALISTIC_IMAGE_MAP[type] || null;
};

/* ═══════════════════════════════════════════════════════════════
   Preview Component Registry
   ═══════════════════════════════════════════════════════════════ */

export const PREVIEW_COMPONENTS = {
  pump: PumpPreview,
  pumps: PumpPreview,
  tank: TankPreview,
  tanks: TankPreview,
  valve: ValvePreview,
  motor: MotorPreview,
  conveyer: ConveyerPreview,
  chiller: ChillerPreview,
  ahu: AHUPreview,
  blowerFan: BlowerFanPreview,
  battery: BatteryPreview,
  msb: MSBPreview,
  transformer: TransformerPreview,
  switchgear: SwitchgearPreview,
  splitACIndoor: SplitACPreview,
  splitAc: SplitACPreview,
  highbayLamp: HighbayLampPreview,
  solarPanel: SolarPanelPreview,
  solarInverter: SolarInverterPreview,
};

/**
 * Get the preview component for a given type.
 * Every type now has a dedicated SVG preview — no more generic fallbacks.
 */
export const getPreviewComponent = (type) => {
  return PREVIEW_COMPONENTS[type] || PumpPreview;
};

export {
  PumpPreview, TankPreview, ValvePreview, MotorPreview,
  ConveyerPreview, AHUPreview, BlowerFanPreview, BatteryPreview,
  ChillerPreview, MSBPreview, TransformerPreview, SwitchgearPreview,
  SplitACPreview, HighbayLampPreview, SolarPanelPreview, SolarInverterPreview,
};

